'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MdClose, MdSearch } from 'react-icons/md';
import axios from 'axios';
import { useTranslations } from 'next-intl';
import NextImage from 'next/image';
import { amplitudeInstance } from '../../config/amplitude';
import buildRouteUrl from '../../helpers/buildRouteUrl';
import buildTitleUrl from '../../helpers/buildTitleUrl';
import useDebounce from '../../hooks/useDebounce';
import useIntl from '../../hooks/useIntl';
import { Link } from '../../i18n/navigation';
import api from '../../services/api';
import type { ProfileImage, ProfileVerification } from '../../types/types';
import VerificationBadge from '../VerificationBadge';
interface BaseTitleSearchResult {
  _id: string;
  short_id: string;
  slugs: {
    slug: string;
    iso_3166_1: string;
  }[];
  title: string;
  original_title?: string;
  release_date?: string;
  poster?: {
    file_path?: string;
  };
}
interface MovieSearchResult extends BaseTitleSearchResult {
  type: 'movie';
}
interface TvSearchResult extends BaseTitleSearchResult {
  type: 'tv';
}
interface PersonSearchResult {
  _id: string;
  type: 'person';
  name: string;
  avatar?: string;
  know_for_department?: string;
}
interface UserSearchResult {
  _id: string;
  type: 'user';
  name: string;
  username: string;
  profile_image?: ProfileImage;
  verifications?: ProfileVerification[];
}
type SearchResult = MovieSearchResult | TvSearchResult | PersonSearchResult | UserSearchResult;
type SearchStatus = 'idle' | 'loading' | 'success' | 'empty' | 'error';
type SearchMode = 'desktop' | 'mobile' | null;
function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function isSearchResult(value: unknown): value is SearchResult {
  if (!isRecord(value) || typeof value._id !== 'string' || typeof value.type !== 'string') {
    return false;
  }
  if (value.type === 'movie' || value.type === 'tv') {
    return typeof value.short_id === 'string' && typeof value.title === 'string' && Array.isArray(value.slugs);
  }
  if (value.type === 'person') return typeof value.name === 'string';
  return value.type === 'user' && typeof value.name === 'string' && typeof value.username === 'string';
}
function getReleaseYear(releaseDate?: string): string | null {
  if (!releaseDate) return null;
  const isoYear = releaseDate.trim().match(/^(\d{4})/);
  if (isoYear) return isoYear[1];
  const year = new Date(releaseDate).getFullYear();
  return Number.isNaN(year) ? null : String(year);
}
function SearchResultItem(t0) {
  const $ = _c(61);
  const {
    result,
    index,
    query,
    optionId,
    isActive,
    optionRef,
    onSelect
  } = t0;
  const t = useTranslations();
  const intl = useIntl();
  let href;
  let imageSrc = "/assets/app/person-fallback.jpg";
  let imageAlt = "";
  let secondaryText = null;
  let isPoster = false;
  if (result.type === "movie" || result.type === "tv") {
    let t1;
    if ($[0] !== intl.locale || $[1] !== result.short_id || $[2] !== result.slugs || $[3] !== result.type) {
      t1 = buildTitleUrl({
        type: result.type,
        locale: intl.locale,
        shortId: result.short_id,
        slugs: result.slugs
      });
      $[0] = intl.locale;
      $[1] = result.short_id;
      $[2] = result.slugs;
      $[3] = result.type;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    href = t1;
    imageSrc = result.poster?.file_path ? `https://cdn-proxy.trakio.app/t/p/w220_and_h330_face${result.poster.file_path}` : "/assets/app/poster-fallback.jpg";
    let t2;
    if ($[5] !== result.title || $[6] !== result.type || $[7] !== t) {
      t2 = result.type === "movie" ? t("images_alts.movie_poster", {
        title: result.title
      }) : t("images_alts.tv_poster", {
        title: result.title
      });
      $[5] = result.title;
      $[6] = result.type;
      $[7] = t;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    imageAlt = t2;
    let t3;
    if ($[9] !== result.release_date) {
      t3 = getReleaseYear(result.release_date);
      $[9] = result.release_date;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    secondaryText = t3;
    isPoster = true;
  } else {
    if (result.type === "person") {
      const t1 = `/person/${result._id}`;
      let t2;
      if ($[11] !== t1) {
        t2 = buildRouteUrl(t1);
        $[11] = t1;
        $[12] = t2;
      } else {
        t2 = $[12];
      }
      href = t2;
      imageSrc = result.avatar ? `https://cdn-proxy.trakio.app/t/p/w220_and_h330_face${result.avatar}` : "/assets/app/person-fallback.jpg";
      let t3;
      if ($[13] !== result.name || $[14] !== t) {
        t3 = t("images_alts.person_image", {
          name: result.name
        });
        $[13] = result.name;
        $[14] = t;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      imageAlt = t3;
      secondaryText = result.know_for_department || null;
    } else {
      if (result.type === "user") {
        const t1 = `/users/${result._id}`;
        let t2;
        if ($[16] !== t1) {
          t2 = buildRouteUrl(t1);
          $[16] = t1;
          $[17] = t2;
        } else {
          t2 = $[17];
        }
        href = t2;
        imageSrc = (result.profile_image && Array.isArray(result.profile_image.versions) ? result.profile_image.versions.find(_temp)?.url : undefined) || "/assets/app/person-fallback.jpg";
        let t3;
        if ($[18] !== result.name || $[19] !== t) {
          t3 = t("images_alts.user_image", {
            name: result.name
          });
          $[18] = result.name;
          $[19] = t;
          $[20] = t3;
        } else {
          t3 = $[20];
        }
        imageAlt = t3;
        secondaryText = `@${result.username}`;
      }
    }
  }
  const title = "title" in result ? result.title : result.name;
  const t1 = href;
  const t2 = `group flex w-full items-center gap-3 rounded-lg p-2 text-left outline-none transition-colors ${isActive ? "bg-white/15 ring-1 ring-inset ring-white/20" : "hover:bg-white/10 focus:bg-white/10 focus:ring-2 focus:ring-inset focus:ring-red-500"} ${index === 0 ? "mt-1" : ""}`;
  let t3;
  if ($[21] !== onSelect || $[22] !== query || $[23] !== result._id || $[24] !== result.type) {
    t3 = () => {
      if (result.type === "person") {
        amplitudeInstance.logEvent("Go To Person Page From Search Result", {
          person_id: result._id,
          search_query: query
        });
      } else {
        amplitudeInstance.logEvent("Go To Content Page From Search Result", {
          content_id: result._id,
          content_type: result.type,
          search_query: query
        });
      }
      onSelect();
    };
    $[21] = onSelect;
    $[22] = query;
    $[23] = result._id;
    $[24] = result.type;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  const t4 = isPoster ? "h-[72px] w-[48px] shrink-0 rounded-md bg-gray-500 object-cover" : "h-12 w-12 shrink-0 rounded-full bg-gray-500 object-cover";
  const t5 = isPoster ? 72 : 48;
  let t6;
  if ($[26] !== imageAlt || $[27] !== imageSrc || $[28] !== t4 || $[29] !== t5) {
    t6 = <NextImage src={imageSrc} alt={imageAlt} className={t4} width={48} height={t5} unoptimized={true} />;
    $[26] = imageAlt;
    $[27] = imageSrc;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const t7 = `search.type.${result.type}`;
  let t8;
  if ($[31] !== t || $[32] !== t7) {
    t8 = t(t7);
    $[31] = t;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== t8) {
    t9 = <span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white/80">{t8}</span>;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== secondaryText) {
    t10 = secondaryText && <span className="truncate text-xs text-white/65">{secondaryText}</span>;
    $[36] = secondaryText;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t9) {
    t11 = <div className="mb-1 flex items-center gap-2">{t9}{t10}</div>;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== title) {
    t12 = <span className="truncate text-sm font-semibold text-white md:text-base">{title}</span>;
    $[41] = title;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== result.type || $[44] !== result.verifications) {
    t13 = result.type === "user" && <VerificationBadge data={result.verifications} />;
    $[43] = result.type;
    $[44] = result.verifications;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== t12 || $[47] !== t13) {
    t14 = <div className="flex min-w-0 items-center gap-1">{t12}{t13}</div>;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t11 || $[50] !== t14) {
    t15 = <div className="min-w-0 flex-1">{t11}{t14}</div>;
    $[49] = t11;
    $[50] = t14;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  let t16;
  if ($[52] !== href || $[53] !== isActive || $[54] !== optionId || $[55] !== optionRef || $[56] !== t15 || $[57] !== t2 || $[58] !== t3 || $[59] !== t6) {
    t16 = <Link ref={optionRef} id={optionId} href={t1} role="option" aria-selected={isActive} prefetch={false} className={t2} onClick={t3}>{t6}{t15}</Link>;
    $[52] = href;
    $[53] = isActive;
    $[54] = optionId;
    $[55] = optionRef;
    $[56] = t15;
    $[57] = t2;
    $[58] = t3;
    $[59] = t6;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  return t16;
}
function _temp(version) {
  return version.resolution === "100x100";
}
function Search() {
  const $ = _c(96);
  const t = useTranslations();
  const intl = useIntl();
  const listboxId = useId();
  const desktopRootRef = useRef(null);
  const desktopInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const optionRefs = useRef(t0);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const [mode, setMode] = useState(null);
  const [query, setQuery] = useState("");
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [results, setResults] = useState(t1);
  const [status, setStatus] = useState("idle");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [retryKey, setRetryKey] = useState(0);
  const debouncedQuery = useDebounce(query, 250);
  let t2;
  if ($[2] !== query) {
    t2 = query.trim();
    $[2] = query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const normalizedQuery = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      requestIdRef.current = requestIdRef.current + 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      optionRefs.current = [];
      setQuery("");
      setResults([]);
      setStatus("idle");
      setActiveIndex(-1);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const resetSearch = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = clear => {
      if (clear) {
        resetSearch();
      }
      setMode(null);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const closeSearch = t4;
  let t5;
  let t6;
  if ($[6] !== mode) {
    t5 = () => {
      if (mode !== "desktop") {
        return;
      }
      const handleOutsideClick = event => {
        if (!desktopRootRef.current?.contains(event.target as Node)) {
          closeSearch(false);
        }
      };
      document.addEventListener("pointerdown", handleOutsideClick);
      return () => document.removeEventListener("pointerdown", handleOutsideClick);
    };
    t6 = [closeSearch, mode];
    $[6] = mode;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== mode) {
    t7 = () => {
      if (mode !== "mobile") {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const focusTimer = window.setTimeout(() => mobileInputRef.current?.focus(), 0);
      return () => {
        window.clearTimeout(focusTimer);
        document.body.style.overflow = previousOverflow;
      };
    };
    t8 = [mode];
    $[9] = mode;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== activeIndex) {
    t9 = () => {
      if (activeIndex < 0) {
        return;
      }
      optionRefs.current[activeIndex]?.scrollIntoView?.({
        block: "nearest"
      });
    };
    t10 = [activeIndex];
    $[12] = activeIndex;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== debouncedQuery || $[16] !== intl.full_language) {
    t11 = () => {
      const searchQuery = debouncedQuery.trim();
      if (!searchQuery) {
        setResults([]);
        setStatus("idle");
        setActiveIndex(-1);
        return;
      }
      const controller = new AbortController();
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      abortControllerRef.current = controller;
      setStatus("loading");
      api.get("/v1/search", {
        headers: {
          "accept-language": intl.full_language
        },
        params: {
          query: searchQuery,
          group_resources: true,
          limit: 20
        },
        signal: controller.signal
      }).then(response => {
        if (controller.signal.aborted || requestId !== requestIdRef.current) {
          return;
        }
        if (!Array.isArray(response.data?.data)) {
          throw new Error("Search response data must be an array");
        }
        const nextResults = response.data.data.filter(isSearchResult);
        optionRefs.current = [];
        setResults(nextResults);
        setActiveIndex(-1);
        setStatus(nextResults.length > 0 ? "success" : "empty");
      }).catch(error => {
        if (controller.signal.aborted || requestId !== requestIdRef.current || axios.isCancel(error)) {
          return;
        }
        setResults([]);
        setActiveIndex(-1);
        setStatus("error");
      });
      return () => controller.abort();
    };
    $[15] = debouncedQuery;
    $[16] = intl.full_language;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== debouncedQuery || $[19] !== intl.full_language || $[20] !== retryKey) {
    t12 = [debouncedQuery, intl.full_language, retryKey];
    $[18] = debouncedQuery;
    $[19] = intl.full_language;
    $[20] = retryKey;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (nextQuery, nextMode) => {
      const limitedQuery = nextQuery.slice(0, 50);
      requestIdRef.current = requestIdRef.current + 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      setQuery(limitedQuery);
      setResults([]);
      setActiveIndex(-1);
      setStatus(limitedQuery.trim() ? "loading" : "idle");
      setMode(nextMode);
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const handleQueryChange = t13;
  let t14;
  if ($[23] !== mode) {
    t14 = () => {
      resetSearch();
      if (mode === "mobile") {
        mobileInputRef.current?.focus();
      } else {
        desktopInputRef.current?.focus();
        setMode("desktop");
      }
    };
    $[23] = mode;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const handleClear = t14;
  let t15;
  if ($[25] !== activeIndex || $[26] !== mode || $[27] !== results || $[28] !== status) {
    t15 = event_0 => {
      if (event_0.key === "Escape") {
        event_0.preventDefault();
        closeSearch(mode === "mobile");
        return;
      }
      if (status !== "success" || results.length === 0) {
        return;
      }
      if (event_0.key === "ArrowDown") {
        event_0.preventDefault();
        setActiveIndex(current => (current + 1) % results.length);
      } else {
        if (event_0.key === "ArrowUp") {
          event_0.preventDefault();
          setActiveIndex(current_0 => current_0 <= 0 ? results.length - 1 : current_0 - 1);
        } else {
          if (event_0.key === "Enter" && activeIndex >= 0) {
            event_0.preventDefault();
            optionRefs.current[activeIndex]?.click();
          }
        }
      }
    };
    $[25] = activeIndex;
    $[26] = mode;
    $[27] = results;
    $[28] = status;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const handleKeyDown = t15;
  let t16;
  if ($[30] !== results || $[31] !== status || $[32] !== t) {
    t16 = () => {
      if (status === "loading") {
        return t("search.loading");
      }
      if (status === "empty") {
        return t("search.empty_title");
      }
      if (status === "error") {
        return t("search.error_title");
      }
      if (status === "success") {
        return t("search.result_count", {
          count: results.length
        });
      }
      return t("search.start_typing");
    };
    $[30] = results;
    $[31] = status;
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const getStatusMessage = t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[34] !== activeIndex || $[35] !== debouncedQuery || $[36] !== handleClear || $[37] !== handleKeyDown || $[38] !== listboxId || $[39] !== mode || $[40] !== normalizedQuery || $[41] !== query || $[42] !== results || $[43] !== status || $[44] !== t) {
    const renderPanel = () => <div id={listboxId} role="listbox" aria-label={t("search.results_aria_label")} aria-busy={status === "loading"} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">{status === "idle" && <div className="flex min-h-40 flex-col items-center justify-center px-6 text-center text-sm text-white/70"><MdSearch size={28} className="mb-3 text-white/60" />{t("search.start_typing")}</div>}{status === "loading" && <div className="flex min-h-40 flex-col items-center justify-center text-sm text-white/75"><span className="mb-3 h-6 w-6 animate-spin rounded-full border-2 border-gray-500 border-t-red-500" />{t("search.loading")}</div>}{status === "empty" && <div className="flex min-h-40 flex-col items-center justify-center px-6 text-center"><span className="font-semibold text-white">{t("search.empty_title")}</span><span className="mt-1 text-sm text-white/70">{t("search.empty_description", {
            query: debouncedQuery.trim()
          })}</span></div>}{status === "error" && <div className="flex min-h-40 flex-col items-center justify-center px-6 text-center"><span className="font-semibold text-white">{t("search.error_title")}</span><span className="mt-1 text-sm text-white/70">{t("search.error_description")}</span><button type="button" className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400" onClick={() => {
          setStatus("loading");
          setRetryKey(_temp2);
        }}>{t("search.retry")}</button></div>}{status === "success" && results.map((result, index) => <SearchResultItem key={`${result.type}-${result._id}`} result={result} index={index} query={debouncedQuery.trim()} optionId={`${listboxId}-option-${index}`} isActive={activeIndex === index} optionRef={element => {
        optionRefs.current[index] = element;
      }} onSelect={() => closeSearch(true)} />)}</div>;
    const t24 = activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;
    let t25;
    if ($[52] !== t) {
      t25 = t("search.input_aria_label");
      $[52] = t;
      $[53] = t25;
    } else {
      t25 = $[53];
    }
    let t26;
    if ($[54] !== listboxId || $[55] !== t24 || $[56] !== t25) {
      t26 = {
        role: "combobox",
        "aria-autocomplete": "list" as const,
        "aria-controls": listboxId,
        "aria-activedescendant": t24,
        "aria-label": t25,
        autoComplete: "off",
        maxLength: 50,
        spellCheck: false
      };
      $[54] = listboxId;
      $[55] = t24;
      $[56] = t25;
      $[57] = t26;
    } else {
      t26 = $[57];
    }
    const inputAccessibilityProps = t26;
    t17 = desktopRootRef;
    t18 = "flex flex-1 flex-col md:relative";
    t19 = handleKeyDown;
    let t27;
    if ($[58] !== t) {
      t27 = t("search.button.accessibility.aria_label");
      $[58] = t;
      $[59] = t27;
    } else {
      t27 = $[59];
    }
    let t28;
    let t29;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = event_1 => {
        event_1.stopPropagation();
        setMode("mobile");
      };
      t29 = <MdSearch size={24} />;
      $[60] = t28;
      $[61] = t29;
    } else {
      t28 = $[60];
      t29 = $[61];
    }
    if ($[62] !== t27) {
      t20 = <button type="button" aria-label={t27} className="flex items-center rounded-md p-1 text-white/70 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-red-500 md:hidden" onClick={t28}>{t29}</button>;
      $[62] = t27;
      $[63] = t20;
    } else {
      t20 = $[63];
    }
    let t30;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <MdSearch size={22} className="shrink-0 text-white/60" />;
      $[64] = t30;
    } else {
      t30 = $[64];
    }
    const t31 = mode === "desktop";
    let t32;
    if ($[65] !== t) {
      t32 = t("search.input_placeholder");
      $[65] = t;
      $[66] = t32;
    } else {
      t32 = $[66];
    }
    let t33;
    if ($[67] !== normalizedQuery) {
      t33 = () => {
        if (normalizedQuery) {
          setMode("desktop");
        }
      };
      $[67] = normalizedQuery;
      $[68] = t33;
    } else {
      t33 = $[68];
    }
    let t34;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = event_2 => handleQueryChange(event_2.target.value, "desktop");
      $[69] = t34;
    } else {
      t34 = $[69];
    }
    let t35;
    if ($[70] !== inputAccessibilityProps || $[71] !== query || $[72] !== t31 || $[73] !== t32 || $[74] !== t33) {
      t35 = <input {...inputAccessibilityProps} ref={desktopInputRef} value={query} aria-expanded={t31} className="h-full min-w-0 flex-1 border-none bg-transparent px-3 text-sm text-white outline-none placeholder:text-gray-500" placeholder={t32} onFocus={t33} onChange={t34} />;
      $[70] = inputAccessibilityProps;
      $[71] = query;
      $[72] = t31;
      $[73] = t32;
      $[74] = t33;
      $[75] = t35;
    } else {
      t35 = $[75];
    }
    let t36;
    if ($[76] !== handleClear || $[77] !== query || $[78] !== t) {
      t36 = !!query && <button type="button" aria-label={t("search.clear")} className="rounded p-1 text-white/70 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-500" onClick={handleClear}><MdClose size={18} /></button>;
      $[76] = handleClear;
      $[77] = query;
      $[78] = t;
      $[79] = t36;
    } else {
      t36 = $[79];
    }
    if ($[80] !== t35 || $[81] !== t36) {
      t21 = <div className="hidden h-11 items-center rounded-lg border border-white/10 bg-gray-600 px-3 transition-colors focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/30 md:flex">{t30}{t35}{t36}</div>;
      $[80] = t35;
      $[81] = t36;
      $[82] = t21;
    } else {
      t21 = $[82];
    }
    t22 = mode === "desktop" && <div className="absolute left-0 top-[52px] z-50 hidden max-h-[70vh] w-full flex-col overflow-hidden rounded-xl border border-white/10 bg-[#141414] shadow-2xl md:flex">{renderPanel()}</div>;
    t23 = mode === "mobile" && <div role="dialog" aria-modal="true" aria-label={t("search.dialog_aria_label")} className="fixed inset-0 z-[100] flex h-[100dvh] w-screen flex-col bg-[#0f0f0f] md:hidden"><div className="flex items-center gap-2 border-b border-white/10 p-4"><div className="flex h-11 min-w-0 flex-1 items-center rounded-lg border border-white/10 bg-gray-600 px-3 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/30"><MdSearch size={22} className="shrink-0 text-white/60" /><input {...inputAccessibilityProps} ref={mobileInputRef} value={query} aria-expanded="true" className="h-full min-w-0 flex-1 border-none bg-transparent px-3 text-base text-white outline-none placeholder:text-gray-500" placeholder={t("search.input_placeholder")} onChange={event_3 => handleQueryChange(event_3.target.value, "mobile")} />{!!query && <button type="button" aria-label={t("search.clear")} className="rounded p-1 text-white/70 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-500" onClick={handleClear}><MdClose size={18} /></button>}</div><button type="button" aria-label={t("search.close")} className="rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-500" onClick={() => closeSearch(true)}><MdClose size={24} /></button></div>{renderPanel()}</div>;
    $[34] = activeIndex;
    $[35] = debouncedQuery;
    $[36] = handleClear;
    $[37] = handleKeyDown;
    $[38] = listboxId;
    $[39] = mode;
    $[40] = normalizedQuery;
    $[41] = query;
    $[42] = results;
    $[43] = status;
    $[44] = t;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
  } else {
    t17 = $[45];
    t18 = $[46];
    t19 = $[47];
    t20 = $[48];
    t21 = $[49];
    t22 = $[50];
    t23 = $[51];
  }
  let t24;
  if ($[83] !== getStatusMessage) {
    t24 = getStatusMessage();
    $[83] = getStatusMessage;
    $[84] = t24;
  } else {
    t24 = $[84];
  }
  let t25;
  if ($[85] !== t24) {
    t25 = <span className="sr-only" aria-live="polite" aria-atomic="true">{t24}</span>;
    $[85] = t24;
    $[86] = t25;
  } else {
    t25 = $[86];
  }
  let t26;
  if ($[87] !== t17 || $[88] !== t18 || $[89] !== t19 || $[90] !== t20 || $[91] !== t21 || $[92] !== t22 || $[93] !== t23 || $[94] !== t25) {
    t26 = <div ref={t17} className={t18} onKeyDown={t19}>{t20}{t21}{t22}{t23}{t25}</div>;
    $[87] = t17;
    $[88] = t18;
    $[89] = t19;
    $[90] = t20;
    $[91] = t21;
    $[92] = t22;
    $[93] = t23;
    $[94] = t25;
    $[95] = t26;
  } else {
    t26 = $[95];
  }
  return t26;
}
function _temp2(current_1) {
  return current_1 + 1;
}
export default Search;